<template>
  <react-widget />
</template>

<script>
import layout from '@/mixins/layout';

const pageTitles = {
  'app-tickets': 'APPS.SUPPORT.TITLE',
  'app-ticket-detail': 'SUPPORT.TICKET_DETAILS',
  tickets: 'SUPPORT.SUPPORT_TICKETS',
  'new-ticket': 'SUPPORT.NEW_TICKET',
  'ticket-detail': 'SUPPORT.TICKET_DETAILS',
  faq: 'SUPPORT.FAQ',
};

const historyNavigationSubscribers = new Set();
let restoreHistoryNavigation = null;

function emitHistoryNavigation() {
  historyNavigationSubscribers.forEach(callback => callback());
}

function patchHistoryNavigation() {
  const pushState = window.history.pushState;
  const replaceState = window.history.replaceState;

  window.history.pushState = function patchedPushState(...args) {
    const result = pushState.apply(this, args);
    emitHistoryNavigation();
    return result;
  };

  window.history.replaceState = function patchedReplaceState(...args) {
    const result = replaceState.apply(this, args);
    emitHistoryNavigation();
    return result;
  };

  return () => {
    window.history.pushState = pushState;
    window.history.replaceState = replaceState;
  };
}

function subscribeToHistoryNavigation(callback) {
  historyNavigationSubscribers.add(callback);
  window.addEventListener('popstate', callback);

  if (!restoreHistoryNavigation) {
    restoreHistoryNavigation = patchHistoryNavigation();
  }

  return () => {
    historyNavigationSubscribers.delete(callback);
    window.removeEventListener('popstate', callback);

    if (historyNavigationSubscribers.size === 0 && restoreHistoryNavigation) {
      restoreHistoryNavigation();
      restoreHistoryNavigation = null;
    }
  };
}

export default {
  name: 'Login',
  mixins: [layout],

  data() {
    return {
      isSyncingBrowserRoute: false,
      unsubscribeFromHistoryNavigation: null,
    };
  },

  watch: {
    $route() {
      this.setRoutePageTitle();
      this.$nextTick(() => {
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
    },
  },

  created() {
    this.setRoutePageTitle();
  },

  mounted() {
    this.unsubscribeFromHistoryNavigation = subscribeToHistoryNavigation(
      this.handleBrowserLocationChange,
    );
  },

  beforeDestroy() {
    if (this.unsubscribeFromHistoryNavigation) {
      this.unsubscribeFromHistoryNavigation();
    }
  },

  methods: {
    getBrowserFullPath() {
      const { pathname, search, hash } = window.location;
      const base = (this.$router.options.base || '/').replace(/\/$/, '');
      let path = pathname;

      if (base && base !== '/' && path.startsWith(base)) {
        path = path.slice(base.length) || '/';
      }

      return `${path}${search}${hash}`;
    },
    getCurrentRouteName() {
      const {
        route: { name },
      } = this.$router.resolve(this.getBrowserFullPath());

      return name || this.$route.name;
    },
    handleBrowserLocationChange() {
      this.$nextTick(this.syncRouteWithBrowserLocation);
    },
    syncRouteWithBrowserLocation() {
      const fullPath = this.getBrowserFullPath();

      if (this.$route.fullPath === fullPath) {
        this.setRoutePageTitle();
        return;
      }

      if (this.isSyncingBrowserRoute) {
        return;
      }

      this.isSyncingBrowserRoute = true;
      Promise.resolve(this.$router.replace(fullPath))
        .catch(() => {})
        .then(() => {
          this.isSyncingBrowserRoute = false;
          this.setRoutePageTitle();
        });
    },
    setRoutePageTitle() {
      const pageTitle = pageTitles[this.getCurrentRouteName()];

      if (pageTitle) {
        this.setPageTitle(pageTitle);
      }
    },
    onLoginSuccess({ token, user }) {
      this.$store.dispatch('auth/setToken', token);
      this.$store.dispatch('auth/setUser', user);
      this.$router.push('/dashboard');
    },
    onLoginError({ message }) {
      console.error('Login failed:', message);
    },
  },
};
</script>
